import React, { useState, useEffect } from 'react';
import UserLayout from '../components/UserLayout';
import { apiClient } from '../api/api';
import Loader from '../components/Loader';
import './PaidAdsPage.css';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { isPreviewUser } from '../utils/previewAccess';
import {
  TrendingUp,
  TrendingDown,
  Search,
  Filter,
  Download,
  Activity,
  ArrowUpDown,
} from 'lucide-react';
// import { ChevronLeft, ChevronRight } from 'lucide-react'; // Removed: fake pagination (no real multi-page data)
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PaidAdsPage = () => {
  const [data, setData] = useState({ campaigns: [], summary: {}, trends: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedShopId, dateRange, user } = useAuth();
  const { theme } = useTheme();
  const previewUser = isPreviewUser(user);
  const [marketingData, setMarketingData] = useState(null);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [marketingError, setMarketingError] = useState(null);

  // Marketing API (Beta) panel colors -- this block uses inline styles (not the campaign-table
  // CSS class), so it doesn't participate in light-theme.css's cascade overrides and needs its
  // own light/dark values directly.
  const isLight = theme === 'light';
  const betaColors = {
    panelBg: isLight ? '#F9FAFB' : '#111',
    panelBorder: isLight ? '#E5E7EB' : '#333',
    textPrimary: isLight ? '#111827' : '#fff',
    textSecondary: isLight ? '#6B7280' : '#aaa',
    textMuted: isLight ? '#9CA3AF' : '#888',
    textFaint: isLight ? '#9CA3AF' : '#666',
    errorText: isLight ? '#dc2626' : '#f87171',
  };

  useEffect(() => {
    fetchPaidAdsMetrics();
  }, [selectedShopId, dateRange]);

  // Marketing API (Beta) -- RELEASED 2026-10-02 to everyone (previewUser/isPreviewUser now always
  // true, see utils/previewAccess.js). Runs alongside the existing scraper-based fetch above,
  // doesn't replace it.
  // Per Setu (2026-09-29): match the app's existing date-range picker, not a fixed window.
  // FIXED (2026-09-29, real bug found live by Roland): the panel rendered nothing at all for
  // ShopOntel -- root cause was these endpoints resolving the shop from the logged-in account's
  // own brand_id only, ignoring the shop-switcher's selectedShopId entirely, so an admin account
  // viewing a shop it doesn't itself own 404'd, Promise.all rejected, and the panel silently
  // stayed blank forever (no error shown). Now passes selectedShopId through, same as the existing
  // scraper-based fetch above already does.
  useEffect(() => {
    // Wait for selectedShopId to actually resolve before fetching -- Roland's account (internal
    // role, no brand_id of its own) hits this race on first mount: selectedShopId briefly starts
    // null while AuthContext resolves it, and without this guard that split second hard-fails
    // with "No shop linked to this account" instead of quietly waiting like the scraper-based
    // fetch's more forgiving fallback chain does.
    if (!previewUser || !selectedShopId) return;
    fetchMarketingApiData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUser, dateRange, selectedShopId]);

  const fetchMarketingApiData = async () => {
    try {
      setMarketingLoading(true);
      setMarketingError(null);
      const shopIdParam = selectedShopId ? `&shop_id=${encodeURIComponent(selectedShopId)}` : '';
      const rangeAndShop = `range=${encodeURIComponent(dateRange)}${shopIdParam}`;
      const [campaignsRes, adgroupsRes, adsRes, gmvMaxRes, accountInfoRes] = await Promise.all([
        apiClient.get(`/marketing/campaigns?${rangeAndShop}`),
        apiClient.get(`/marketing/adgroups?${rangeAndShop}`),
        apiClient.get(`/marketing/ads?${rangeAndShop}`),
        apiClient.get(`/marketing/gmv-max?promotion_type=PRODUCT_GMV_MAX&${rangeAndShop}`),
        apiClient.get(`/marketing/account-info?${shopIdParam.replace(/^&/, '')}`),
      ]);
      setMarketingData({
        campaigns: campaignsRes.data?.data,
        adgroups: adgroupsRes.data?.data,
        ads: adsRes.data?.data,
        gmvMax: gmvMaxRes.data?.data,
        accountInfo: accountInfoRes.data?.data,
      });
    } catch (error) {
      console.error('Error fetching Marketing API data:', error);
      setMarketingError(error?.response?.data?.detail || 'Failed to load Marketing API data.');
    } finally {
      setMarketingLoading(false);
    }
  };

  const fetchPaidAdsMetrics = async () => {
    try {
      setLoading(true);
      let url = `/paid-ads-metrics?range=${dateRange}`;
      if (selectedShopId) {
        url += `&shop_id=${selectedShopId}`;
      }
      const response = await apiClient.get(url);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching paid ads metrics:', error);
      setError('Failed to load data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const parseCurrency = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string' && val !== '--') return parseFloat(val.replace(/[^0-9.-]+/g,""));
    return 0;
  };

  const sanitizeText = (text) => {
    if (!text) return "";
    // Remove leading/trailing commas, periods, or spaces and ensure first char is capitalize
    let clean = text.trim().replace(/^[,.\s]+|[,.\s]+$/g, "");
    return clean.charAt(0).toUpperCase() + clean.slice(1);
  };

  const renderInsight = (ins, i) => (
    <div key={i} className="recap-insight">
      <div className="insight-emoji">{ins.emoji}</div>
      <div className="insight-content">
        <div className="insight-text-wrapper">
          {sanitizeText(ins.text)}{' '}
          {ins.highlight && (
            <span className="insight-highlight" style={{ color: ins.highlight_color || ins.color }}>
              {ins.highlight}
            </span>
          )}
          {/* prefix is a trailing context clause — render inline after highlight, not as a heading */}
          {ins.prefix && (
            <span style={{ color: ins.color, opacity: 0.75 }}>{' '}{ins.prefix.trim()}</span>
          )}
        </div>
      </div>
    </div>
  );

  const chartData = {
    labels: (data.trends || []).map(t => t.date),
    datasets: [
      {
        label: 'Ad Spend',
        data: (data.trends || []).map(t => t.spend),
        borderColor: '#3b82f6',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.1)');
          gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Revenue',
        data: (data.trends || []).map(t => t.revenue),
        borderColor: '#10b981',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.25)');
          gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#111',
        titleColor: '#888',
        bodyColor: '#fff',
        borderColor: '#333',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
      }
    },
    hover: {
      mode: 'index',
      intersect: false
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#555',
          font: {
            size: 11,
            weight: '500'
          },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7
        },
        border: {
          display: false
        }
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.03)',
          drawBorder: false,
        },
        ticks: {
          color: '#555',
          font: {
            size: 11,
            weight: '500'
          },
          stepSize: 200, // Matching screenshot increments if possible
          callback: (val) => val.toLocaleString()
        },
        border: {
          display: false,
          dash: [4, 4]
        }
      }
    }
  };



  return (
    <UserLayout>
      {error && (
        <div style={{background:'#2d1a1a',border:'1px solid #7f1d1d',color:'#fca5a5',padding:'12px 16px',borderRadius:'8px',margin:'16px 16px 0'}}>
          {error}
        </div>
      )}
      <div className="page-relative-wrapper">
        <div className="paid-ads-container">
        {/* Redesigned AI Campaign Insights Widget */}
        <div className={`ai-recap-card ${loading ? 'shimmer skeleton' : ''}`}>
          <div className="recap-header">
            <div className="recap-header-left">
              <div className="recap-icon-box">
                <div className="inner-icon-bg">
                  <Activity size={24} color="#3b82f6" />
                </div>
              </div>
              <div>
                <div className="recap-title">{data?.ai_recap?.title || 'AI Campaign Insights'}</div>
                <div className="recap-subtitle">{data?.ai_recap?.subtitle || 'Last 30 Days Performance Analysis'}</div>
              </div>
            </div>
            <div className="status-badge">{data?.ai_recap?.status_badge || 'Active'}</div>
          </div>
          <div className="recap-body">
            {(() => {
              const insights = data?.ai_recap?.insights || [];
              
              if (insights.length === 0 && data.campaigns && data.campaigns.length > 0) {
                const sortedByRoas = [...data.campaigns].sort((a, b) => (b.roas || 0) - (a.roas || 0));
                const topCamp = sortedByRoas[0];
                const avgRoi = data.summary?.roi || 0;
                const revGrowth = data.summary?.revenue_growth || '0%';
                
                const dynamicInsights = [
                  {
                    emoji: '🎯',
                    prefix: 'Top Performer',
                    text: `Your campaign "${topCamp.name}" is leading with a ROAS of`,
                    highlight: `${(topCamp.roas || 0).toFixed(2)}x`,
                    color: '#10b981',
                    highlight_color: '#10b981'
                  },
                  {
                    emoji: '📈',
                    prefix: 'Scaling',
                    text: `Revenue has grown by`,
                    highlight: revGrowth,
                    color: revGrowth.includes('-') ? '#ef4444' : '#10b981',
                    highlight_color: revGrowth.includes('-') ? '#ef4444' : '#10b981'
                  },
                  {
                    emoji: '⚡',
                    prefix: 'Efficiency',
                    text: `Overall ROI is steady at`,
                    highlight: `${avgRoi.toFixed(2)}x`,
                    color: avgRoi >= 2 ? '#3b82f6' : '#f59e0b',
                    highlight_color: avgRoi >= 2 ? '#3b82f6' : '#f59e0b'
                  }
                ];

                if (parseCurrency(data.summary?.cost_per_order || 0) > 0) {
                  dynamicInsights.push({
                    emoji: '💸',
                    prefix: 'Cost Check',
                    text: `Average Cost per Order is`,
                    highlight: formatCurrency(data.summary?.cost_per_order),
                    color: '#a855f7',
                    highlight_color: '#a855f7'
                  });
                }

                return dynamicInsights.map((ins, i) => renderInsight(ins, i));
              }

              return insights.map((ins, i) => renderInsight(ins, i));
            })()}
          </div>
        </div>

        {/* Metric Cards */}
        <div className="metric-grid">
          <div className={`metric-card blue ${loading ? 'shimmer skeleton' : ''}`}>
            <div className="card-label">
              <div className="label-dot" style={{ backgroundColor: '#3b82f6' }} /> Ad Spend
            </div>
            <div className="card-value">{formatCurrency(data.summary?.ad_spend || 0)}</div>
            <div className="trend-container">
              <div className={`trend-badge ${data.summary?.ad_spend_growth?.includes('-') ? 'down' : data.summary?.ad_spend_growth?.includes('+') ? 'up' : 'neutral'}`}>
                {data.summary?.ad_spend_growth?.includes('-') ? <TrendingDown size={14} /> : data.summary?.ad_spend_growth?.includes('+') ? <TrendingUp size={14} /> : null}
                {data.summary?.ad_spend_growth?.replace('+','').replace('-','') || 'N/A'}
              </div>
              <div className="trend-label">vs last period</div>
            </div>
          </div>
          {data.summary?.roi > 0 && (
            <div className={`metric-card orange ${loading ? 'shimmer skeleton' : ''}`}>
              <div className="card-label">
                <div className="label-dot" style={{ backgroundColor: '#f97316' }} /> ROI
              </div>
              <div className="card-value">{(data.summary.roi).toFixed(2)}x</div>
              <div className="trend-container">
                <div className={`trend-badge ${data.summary?.roi_growth?.includes('-') ? 'down' : data.summary?.roi_growth?.includes('+') ? 'up' : 'neutral'}`}>
                  {data.summary?.roi_growth?.includes('-') ? <TrendingDown size={14} /> : data.summary?.roi_growth?.includes('+') ? <TrendingUp size={14} /> : null}
                  {data.summary?.roi_growth?.replace('+','').replace('-','') || 'N/A'}
                </div>
                <div className="trend-label">vs last period</div>
              </div>
            </div>
          )}
          {data.summary?.cost_per_order > 0 && (
            <div className={`metric-card purple ${loading ? 'shimmer skeleton' : ''}`}>
              <div className="card-label">
                <div className="label-dot" style={{ backgroundColor: '#a855f7' }} /> Cost per Order
              </div>
              <div className="card-value">{formatCurrency(data.summary.cost_per_order)}</div>
              <div className="trend-container">
                {/* CPO: rising cost is bad (red), falling is good (green) — intentionally inverted */}
                <div className={`trend-badge ${data.summary?.cpo_growth?.includes('+') ? 'down' : data.summary?.cpo_growth?.includes('-') ? 'up' : 'neutral'}`}>
                  {data.summary?.cpo_growth?.includes('+') ? <TrendingDown size={14} /> : data.summary?.cpo_growth?.includes('-') ? <TrendingUp size={14} /> : null}
                  {data.summary?.cpo_growth?.replace('+','').replace('-','') || 'N/A'}
                </div>
                <div className="trend-label">vs last period</div>
              </div>
            </div>
          )}
          {data.summary?.revenue > 0 && (
            <div className={`metric-card green ${loading ? 'shimmer skeleton' : ''}`}>
              <div className="card-label">
                <div className="label-dot" style={{ backgroundColor: '#10b981' }} /> Revenue
              </div>
              <div className="card-value">{formatCurrency(data.summary.revenue)}</div>
              <div className="trend-container">
                <div className={`trend-badge ${data.summary?.revenue_growth?.includes('-') ? 'down' : data.summary?.revenue_growth?.includes('+') ? 'up' : 'neutral'}`}>
                  {data.summary?.revenue_growth?.includes('-') ? <TrendingDown size={14} /> : data.summary?.revenue_growth?.includes('+') ? <TrendingUp size={14} /> : null}
                  {data.summary?.revenue_growth?.replace('+','').replace('-','') || 'N/A'}
                </div>
                <div className="trend-label">vs last period</div>
              </div>
            </div>
          )}
        </div>

        {data.summary?.ads_exceeds_shop_total && (
          <div className="ads-quirk-note">
            Ads Orders may show higher than this shop's Total Orders for the same period — a known TikTok display-timezone difference between the Ads dashboard and shop reporting, not a data error.
          </div>
        )}

        {/* Trends Chart */}
        <div className={`chart-section ${loading ? 'shimmer skeleton' : ''}`}>
          <div className="chart-header">
            <div>
              <div className="chart-title">Ad Performance Trends</div>
              <div className="chart-subtitle">Ad Spend vs Revenue Over <span className="highlight-text">Last 7 Days</span></div>
            </div>
            <button className="export-btn">
              <Download size={16} /> Export
            </button>
          </div>
          <div style={{ height: '300px', position: 'relative' }}>
             <Line data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Redesigned Active Campaigns Widget */}
        <div className={`campaigns-section ${loading ? 'shimmer skeleton' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">Active Campaigns</h2>
            <p className="section-subtitle">Manage and monitor your advertising campaigns</p>
          </div>

          <div className="controls-row">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input 
                type="text" 
                placeholder="Search campaigns..." 
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="action-buttons">
              <button className="icon-btn">
                <Filter size={20} />
              </button>
              <button className="icon-btn">
                <ArrowUpDown size={20} />
              </button>
            </div>
          </div>

          <table className="campaign-table">
            <thead>
              <tr>
                <th style={{ width: '25%' }}>Campaign Name</th>
                <th style={{ width: '15%' }}>Period</th>
                <th style={{ width: '12%' }}>Status</th>
                <th style={{ width: '12%' }}>Budget</th>
                <th style={{ width: '12%' }}>ROAS</th>
                <th style={{ width: '12%' }}>Conversions</th>
                <th style={{ width: '12%' }}>GMV</th>
              </tr>
            </thead>
            <tbody>
              {(data.campaigns || [])
                .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map((campaign, idx) => {
                  const statusClass = campaign.status?.toLowerCase() === 'active' ? 'active' : 'review';
                  // const roasTrend = idx % 2 === 0 ? 'up' : 'down'; // Removed: mocked trend direction — restore when real ROAS prev-period data available

                  return (
                    <tr key={idx}>
                      <td>
                        <div className="camp-main-name">{campaign.name}</div>
                        <div className="camp-sub-text">Ads</div>
                      </td>
                      <td>
                        <div className="camp-main-name" style={{ fontWeight: 400, fontSize: '14px' }}>{campaign.schedule || '—'}</div>
                        {/* <div className="camp-sub-text">30 days remaining</div> */}
                      </td>
                      <td>
                        <div className={`status-pill ${statusClass}`}>
                          <div className={`dot ${statusClass}`} />
                          {campaign.status}
                        </div>
                      </td>
                      <td>
                        {/* Budget cap from TikTok campaign table — per-campaign spend not available in the table */}
                        <div className="spend-amount">{campaign.budget || '—'}</div>
                      </td>
                      <td>
                        {/* Per-campaign ROAS not in TikTok Ads table; aggregate ROAS shown in summary cards above */}
                        <div className="roas-amount" style={{ color: 'var(--text-muted, #6b7280)' }}>—</div>
                      </td>
                      <td>
                        {/* Per-campaign conversions not in TikTok Ads table */}
                        <div className="conv-count" style={{ color: 'var(--text-muted, #6b7280)' }}>—</div>
                      </td>
                      <td>
                        {/* Per-campaign GMV not in TikTok Ads table */}
                        <div className="gmv-value" style={{ color: 'var(--text-muted, #6b7280)' }}>—</div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          
          <div className="pagination-row">
            <div>Showing {data.campaigns?.length || 0} campaigns</div>
            {/*
            <div className="pagination-controls">
              <button className="page-btn arrow"><ChevronLeft size={18} /></button>
              <button className="page-btn active">1</button>
              <button className="page-btn">2</button>
              <button className="page-btn">3</button>
              <button className="page-btn arrow"><ChevronRight size={18} /></button>
            </div>
            */}
          </div>
        </div>

        {previewUser && (
          <div style={{ marginTop: '24px', border: `1px solid ${betaColors.panelBorder}`, borderRadius: '12px', padding: '20px', background: betaColors.panelBg }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: 600, color: betaColors.textPrimary }}>Marketing API (Beta)</div>
              <div style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: '#3b82f622', color: '#3b82f6' }}>Beta</div>
            </div>

            {marketingLoading && <div style={{ color: betaColors.textMuted }}>Loading real Marketing API data...</div>}

            {!marketingLoading && marketingError && (
              <div style={{ color: betaColors.errorText }}>Failed to load: {marketingError}</div>
            )}

            {!marketingLoading && !marketingError && marketingData && !marketingData.accountInfo?.connected && (
              <div style={{ color: betaColors.textMuted }}>Not connected. Connect this shop in Settings &gt; Linked Accounts &gt; Ads.</div>
            )}

            {!marketingLoading && marketingData?.accountInfo?.connected && (
              <>
                <div style={{ color: betaColors.textSecondary, fontSize: '13px', marginBottom: '16px' }}>
                  Connected: {marketingData.accountInfo.advertiser_name || marketingData.accountInfo.advertiser_id}
                  {marketingData.accountInfo.business_centers?.[0]?.name && ` — Business Center: ${marketingData.accountInfo.business_centers[0].name}`}
                </div>

                <div style={{ fontSize: '13px', fontWeight: 600, color: betaColors.textPrimary, marginBottom: '8px' }}>
                  Campaigns ({marketingData.campaigns?.campaigns?.length || 0})
                </div>
                {(marketingData.campaigns?.campaigns?.length || 0) === 0 ? (
                  <div style={{ color: betaColors.textFaint, fontSize: '13px', marginBottom: '16px' }}>No campaigns on this account yet.</div>
                ) : (
                  <table className="campaign-table" style={{ marginBottom: '16px' }}>
                    <thead><tr><th>Name</th><th>Objective</th><th>Spend</th><th>Purchases</th><th>CPA</th><th>ROAS</th></tr></thead>
                    <tbody>
                      {marketingData.campaigns.campaigns.map((c, i) => (
                        <tr key={i}>
                          <td>{c.campaign_name || '—'}</td>
                          <td>{c.objective_type || '—'}</td>
                          <td>{c.spend ?? '—'}</td>
                          <td>{c.purchases ?? '—'}</td>
                          <td>{c.cpa ?? '—'}</td>
                          <td>{c.roas ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div style={{ fontSize: '13px', fontWeight: 600, color: betaColors.textPrimary, marginBottom: '8px' }}>
                  Ad Groups ({marketingData.adgroups?.adgroups?.length || 0})
                </div>
                {(marketingData.adgroups?.adgroups?.length || 0) === 0 ? (
                  <div style={{ color: betaColors.textFaint, fontSize: '13px', marginBottom: '16px' }}>No ad groups on this account yet.</div>
                ) : (
                  <table className="campaign-table" style={{ marginBottom: '16px' }}>
                    <thead><tr><th>Name</th><th>Campaign</th><th>Spend</th><th>Clicks</th><th>ROAS</th></tr></thead>
                    <tbody>
                      {marketingData.adgroups.adgroups.map((a, i) => (
                        <tr key={i}>
                          <td>{a.adgroup_name || '—'}</td>
                          <td>{a.campaign_id || '—'}</td>
                          <td>{a.spend ?? '—'}</td>
                          <td>{a.clicks ?? '—'}</td>
                          <td>{a.roas ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div style={{ fontSize: '13px', fontWeight: 600, color: betaColors.textPrimary, marginBottom: '8px' }}>
                  Ads ({marketingData.ads?.ads?.length || 0})
                </div>
                {(marketingData.ads?.ads?.length || 0) === 0 ? (
                  <div style={{ color: betaColors.textFaint, fontSize: '13px', marginBottom: '16px' }}>No ads on this account yet.</div>
                ) : (
                  <table className="campaign-table" style={{ marginBottom: '16px' }}>
                    <thead><tr><th>Name</th><th>Ad Group</th><th>Spend</th><th>Clicks</th><th>ROAS</th></tr></thead>
                    <tbody>
                      {marketingData.ads.ads.map((a, i) => (
                        <tr key={i}>
                          <td>{a.ad_name || '—'}</td>
                          <td>{a.adgroup_id || '—'}</td>
                          <td>{a.spend ?? '—'}</td>
                          <td>{a.clicks ?? '—'}</td>
                          <td>{a.roas ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div style={{ fontSize: '13px', fontWeight: 600, color: betaColors.textPrimary, marginBottom: '8px' }}>
                  GMV Max — Product ({marketingData.gmvMax?.campaigns?.length || 0})
                </div>
                {(marketingData.gmvMax?.campaigns?.length || 0) === 0 ? (
                  <div style={{ color: betaColors.textFaint, fontSize: '13px' }}>No GMV Max campaigns on this account yet.</div>
                ) : (
                  <table className="campaign-table">
                    <thead><tr><th>Name</th><th>Status</th><th>Spend</th><th>Revenue</th><th>Orders</th><th>Cost/Order</th><th>ROI</th></tr></thead>
                    <tbody>
                      {marketingData.gmvMax.campaigns.map((c, i) => (
                        <tr key={i}>
                          <td>{c.campaign_name || '—'}</td>
                          <td>{c.operation_status || '—'}</td>
                          <td>{c.spend != null ? `$${c.spend}` : '—'}</td>
                          <td>{c.gross_revenue != null ? `$${c.gross_revenue}` : '—'}</td>
                          <td>{c.orders ?? '—'}</td>
                          <td>{c.cost_per_order != null ? `$${c.cost_per_order}` : '—'}</td>
                          <td>{c.roi != null ? `${c.roi}x` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  </UserLayout>
);
};

export default PaidAdsPage;
